import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Grid,
  Paper,
  Typography,
  makeStyles,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Box,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Collapse
} from '@material-ui/core';
import {
  People as PeopleIcon,
  Map as MapIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  NavigateNext as NavigateNextIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DirectionsCar as DirectionsCarIcon,
  Home as HomeIcon
} from '@material-ui/icons';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  LineChart, Line, Area, AreaChart
} from 'recharts';
import { Link } from 'react-router-dom';
import moment from 'moment';
import TerritoryMapPreview from './TerritoryMapPreview'
import WorkorderTrendChart from './WorkorderTrend';
import WorkorderDetailModal from './WorkorderDetailModal';
import Icon from '@mdi/react';
import { mdiAccount } from '@mdi/js';
// Define styles using Material UI's makeStyles
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    }
  },
  dashboardTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: '100%'
  },
  fixedHeight: {
    height: 240,
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: theme.spacing(1, 2),
  },
  warningHeader: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
  },
  successHeader: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
  },
  infoHeader: {
    backgroundColor: theme.palette.info.main,
    color: '#fff',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(2),
    position: 'relative',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: theme.shadows[6],
      cursor: 'pointer',
    },
  },
  avatar: {
    backgroundColor: (props) => props.color || theme.palette.primary.main,
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginBottom: theme.spacing(1),
  },
  avatarIcon: {
    fontSize: '2rem',
  },
  mapPreviewContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
    minHeight: 200,
  },
  mapPreviewOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    padding: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderTopLeftRadius: theme.shape.borderRadius,
  },
  listItemWithBorder: {
    borderLeft: '4px solid',
    marginBottom: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
  },
  progressWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
  },
  tabRoot: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  priorityHigh: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
  },
  priorityMedium: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
  },
  priorityLow: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  cardAction: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
  },
  badge: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  calendarItem: {
    position: 'relative',
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
      cursor: 'pointer',
    },
  },
  dateChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    marginRight: theme.spacing(1),
  },
  cardContent: {
    padding: theme.spacing(0),
    "&:last-child": {
      paddingBottom: 0
    }
  },
  viewMoreButton: {
    margin: theme.spacing(1),
  },
  propertyAvatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    backgroundColor: theme.palette.grey[200],
  },
  issueIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.error.main,
  },
  overviewSection: {
    marginBottom: theme.spacing(4),
  },
  cardHeaderAction: {
    margin: 0,
  }
}));

const generateDistinctColorMap = (crews) => {
  const colorMap = {};
  const total = crews.length;

  // Generate shuffled hue steps for max visual separation
  const hueSteps = [];
  for (let i = 0; i < total; i++) {
    const hue = Math.round((360 / total) * i);
    hueSteps.push(hue);
  }

  // Shuffle hue order to spread visually similar colors apart
  const visuallyDistinctOrder = [0, 180, 90, 270, 45, 225, 135, 315]; // works great for up to 8
  const hues = hueSteps.map((_, i) => visuallyDistinctOrder[i % visuallyDistinctOrder.length]);

  crews.forEach((emp, index) => {
    const hue = hues[index % hues.length]; // wrap if more than 8
    colorMap[emp._id] = `hsl(${hue}, 70%, 50%)`;
  });

  return colorMap;
};

const IrrigationDashboard = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [territories, setTerritories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [issues, setIssues] = useState([]);
  const [recentChecks, setRecentChecks] = useState([]);
  const [upcomingChecks, setUpcomingChecks] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [propertyMaps, setPropertyMaps] = useState([]);
  const [properties, setProperties] = useState([]);
  const [territoryStats, setTerritoryStats] = useState([]);
  const [employeeColorMap, setEmployeeColorMap] = useState({});
  const [crewColorMap, setCrewColorMap] = useState({})
  const [chartData, setChartData] = useState([])
  const [crews, setCrews] = useState()
  const [selectedWorkorder, setSelectedWorkorder] = useState(null);
  const [workorderModalOpen, setWorkorderModalOpen] = useState(false);
  // Dashboard stats
  const [stats, setStats] = useState({
    totalTerritories: 0,
    totalProperties: 0,
    propertiesWithMaps: 0,
    outstandingIssues: 0,
    openWorkOrders: 0,
    completedChecksThisWeek: 0,
    scheduledChecksNextWeek: 0
  });

  // No Irrigation modal state
  const [noIrrigationModalOpen, setNoIrrigationModalOpen] = useState(false);
  const [noIrrigationSites, setNoIrrigationSites] = useState([]);
  const [noIrrigationLoading, setNoIrrigationLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Tab states
  const [issueTabValue, setIssueTabValue] = useState(0);
  const [checkTabValue, setCheckTabValue] = useState(0);

  // Inspection schedule state
  const [inspectionSchedule, setInspectionSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [scheduleDayDetail, setScheduleDayDetail] = useState(null);
  
const fetchDashboardData = async () => {
  console.log('Fetch Dashboard Data...')
  setLoading(true);
  try {
    const response = await axios.get(`${props.url}/api/hmi/genericGetRequest?type=IRRIGATION_DASHBOARD`);
    if (response?.data?.success && response?.data?.data) {
      const dashboardData = response.data.data;
      console.log('Set our dashboard data...', dashboardData)
      // Set basic data
      setTerritories(dashboardData.existingTerritories || []);
      setEmployees(dashboardData.irrigationTechs || []);
      setIssues(dashboardData.issues || []);
      setWorkOrders(dashboardData.workorders || []);
      setRecentChecks(dashboardData.recentChecks || []);
      setUpcomingChecks(dashboardData.upcomingChecks || []);
      setProperties(dashboardData.properties || []);
      setPropertyMaps(dashboardData.propertiesWithMaps || []);
      setTerritoryStats(dashboardData.territoryStats || []);
      setStats(dashboardData.stats || {});
      setChartData(dashboardData.chartData || [])
      // completedWorkordersStatsByEmployee
      // Generate color map for employees if not provided
      if (dashboardData.irrigationCrews && dashboardData.irrigationCrews.length) {
        const colorMap = generateDistinctColorMap(dashboardData.irrigationCrews);
        setCrewColorMap(colorMap);
        setCrews(dashboardData.irrigationCrews)
      }
    }
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
  } finally {
    setLoading(false);
  }
};

  const fetchInspectionSchedule = async () => {
    setScheduleLoading(true);
    try {
      const response = await axios.post(
        `${props.url}/api/hmi/genericPostRequest?type=GET_IRRIGATION_INSPECTION_SCHEDULE`,
        { numDays: 5 }
      );
      if (response?.data?.success) {
        setInspectionSchedule(response.data.data);
        // Auto-expand the first day
        if (response.data.data?.schedule?.length) {
          setExpandedDays({ 0: true });
        }
      }
    } catch (e) {
      console.error('Error fetching inspection schedule', e);
    } finally {
      setScheduleLoading(false);
    }
  };

  // const fetchIrrigationTechsLastLocation = async () => {
  //   try {
  //     const response = await axios.get(`${props.url}/api/hmi/genericGetRequest?type=IRRIGATION_TECH_LOCATIONS`);
  //     console.log('Got Irrigation tech last locs..', response)
  //     // if (response?.data?.success && response?.data?.data) {
  //   } catch (e) {
  //     console.log('Error 330', e)
  //   }
  // }

  React.useEffect(() => {
    fetchDashboardData()
    fetchInspectionSchedule()
    // fetchIrrigationTechsLastLocation()
  }, [])

  // Mock data generator functions
  const generateMockData = () => {
    // Properties data
    const properties = [];
    const propertyNames = [
      'Oakwood Estates', 'Highland Gardens', 'Riverside Complex', 
      'Evergreen Meadows', 'Sunny Acres', 'Maple Ridge', 
      'Cedar Heights', 'Pine Valley', 'Willow Creek', 'Blue Horizon'
    ];
    
    for (let i = 0; i < 50; i++) {
      properties.push({
        _id: `prop_${i}`,
        name: propertyNames[i % propertyNames.length] + ` ${Math.floor(i / 10) + 1}`,
        address: `${1000 + i} Main St, Anytown, USA`,
        territoryId: `territory_${i % 5}`,
        hasAssetMap: Math.random() > 0.4,
      });
    }
    
    // Territories
    const territoryNames = ['North', 'South', 'East', 'West', 'Central'];
    const territories = territoryNames.map((name, i) => ({
      _id: `territory_${i}`,
      name: `${name} Territory`,
      employeeId: i < 4 ? `emp_${i}` : null,
      propertyCount: properties.filter(p => p.territoryId === `territory_${i}`).length
    }));
    
    // Employees
    const employees = [];
    const firstNames = ['John', 'Sarah', 'Michael', 'Emma', 'David'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones'];
    
    for (let i = 0; i < 5; i++) {
      employees.push({
        _id: `emp_${i}`,
        firstName: firstNames[i],
        lastName: lastNames[i],
        position: 'Irrigation Technician',
        territories: territories.filter(t => t.employeeId === `emp_${i}`).map(t => t._id)
      });
    }
    
    // Issues
    const issueTypes = ['Broken Sprinkler', 'Leaking Valve', 'Controller Malfunction', 'Low Water Pressure', 'Misaligned Head'];
    const issues = [];
    
    for (let i = 0; i < 15; i++) {
      const priority = i % 3; // 0 = high, 1 = medium, 2 = low
      const property = properties[Math.floor(Math.random() * properties.length)];
      
      issues.push({
        _id: `issue_${i}`,
        property: property,
        type: issueTypes[i % issueTypes.length],
        description: `${issueTypes[i % issueTypes.length]} in zone ${(i % 8) + 1}`,
        priority: priority === 0 ? 'High' : priority === 1 ? 'Medium' : 'Low',
        reportDate: moment().subtract(Math.floor(Math.random() * 7), 'days').format('YYYY-MM-DD'),
        status: i < 10 ? 'Open' : 'In Progress'
      });
    }
    
    // Recent Checks
    const recentChecks = [];
    for (let i = 0; i < 10; i++) {
      const property = properties[Math.floor(Math.random() * properties.length)];
      const employee = employees[Math.floor(Math.random() * employees.length)];
      
      recentChecks.push({
        _id: `check_${i}`,
        property: property,
        employee: employee,
        date: moment().subtract(i, 'days').format('YYYY-MM-DD'),
        status: 'Completed',
        issues: Math.floor(Math.random() * 3),
        notes: Math.random() > 0.7 ? 'System operating normally' : 'Adjustments made to zones 2 and 4'
      });
    }
    
    // Upcoming Checks
    const upcomingChecks = [];
    for (let i = 0; i < 12; i++) {
      const property = properties[Math.floor(Math.random() * properties.length)];
      const employee = employees[Math.floor(Math.random() * employees.length)];
      
      upcomingChecks.push({
        _id: `upcoming_${i}`,
        property: property,
        employee: employee,
        date: moment().add(i, 'days').format('YYYY-MM-DD'),
        status: 'Scheduled',
        checkType: Math.random() > 0.3 ? 'Routine' : 'Follow-up'
      });
    }
    
    // Work Orders
    const workOrders = [];
    const workOrderTypes = ['Repair', 'Maintenance', 'Installation', 'Adjustment', 'Inspection'];
    
    for (let i = 0; i < 8; i++) {
      const property = properties[Math.floor(Math.random() * properties.length)];
      const employee = employees[Math.floor(Math.random() * employees.length)];
      
      workOrders.push({
        _id: `wo_${i}`,
        property: property,
        type: workOrderTypes[i % workOrderTypes.length],
        description: `${workOrderTypes[i % workOrderTypes.length]} - ${issueTypes[Math.floor(Math.random() * issueTypes.length)]}`,
        assignedTo: employee,
        priority: i % 3 === 0 ? 'High' : i % 3 === 1 ? 'Medium' : 'Low',
        status: 'Open',
        createdDate: moment().subtract(Math.floor(Math.random() * 14), 'days').format('YYYY-MM-DD'),
        scheduledDate: moment().add(Math.floor(Math.random() * 7), 'days').format('YYYY-MM-DD')
      });
    }
    
    // Properties with asset maps
    const propertyMaps = properties.filter(p => p.hasAssetMap);
    
    // Calculate stats
    const stats = {
      totalTerritories: territories.length,
      totalProperties: properties.length,
      propertiesWithMaps: propertyMaps.length,
      outstandingIssues: issues.filter(i => i.status === 'Open' || i.status === 'In Progress').length,
      openWorkOrders: workOrders.length,
      completedChecksThisWeek: recentChecks.filter(c => 
        moment(c.date).isAfter(moment().subtract(7, 'days'))
      ).length,
      scheduledChecksNextWeek: upcomingChecks.filter(c => 
        moment(c.date).isBefore(moment().add(7, 'days'))
      ).length,
    };
    
    return {
      territories,
      employees,
      issues,
      recentChecks,
      upcomingChecks,
      workOrders,
      propertyMaps,
      stats
    };
  };

  // Fetch data from server or use mock data
  // useEffect(() => {
  //   const fetchData = async () => {
  //     setLoading(true);
  //     try {
  //       // In a real implementation, you would fetch data from your API
  //       // For now, let's use mock data
  //       const mockData = generateMockData();
        
  //       setTerritories(mockData.territories);
  //       setEmployees(mockData.employees);
  //       setIssues(mockData.issues);
  //       setRecentChecks(mockData.recentChecks);
  //       setUpcomingChecks(mockData.upcomingChecks);
  //       setWorkOrders(mockData.workOrders);
  //       setPropertyMaps(mockData.propertyMaps);
  //       setStats(mockData.stats);
        
  //       // Simulate API delay
  //       setTimeout(() => {
  //         setLoading(false);
  //       }, 1000);
        
  //     } catch (error) {
  //       console.error('Error fetching dashboard data:', error);
  //       setLoading(false);
  //     }
  //   };
    
  //   fetchData();
  // }, []);
  
  // Handle tab changes
  const handleIssueTabChange = (event, newValue) => {
    setIssueTabValue(newValue);
  };
  
  const handleCheckTabChange = (event, newValue) => {
    setCheckTabValue(newValue);
  };

  const toggleDayExpanded = (dayIndex) => {
    setExpandedDays(prev => ({ ...prev, [dayIndex]: !prev[dayIndex] }));
  };

  // No Irrigation handlers
  const fetchNoIrrigationSites = async () => {
    setNoIrrigationLoading(true);
    try {
      const response = await axios.get(`${props.url}/api/hmi/genericGetRequest?type=NO_IRRIGATION_JOBSITES`);
      if (response?.data?.success) {
        setNoIrrigationSites(response.data.data || []);
      }
    } catch (e) {
      console.error('Error fetching no-irrigation sites', e);
    } finally {
      setNoIrrigationLoading(false);
    }
  };

  const handleOpenNoIrrigationModal = () => {
    setNoIrrigationModalOpen(true);
    fetchNoIrrigationSites();
  };

  const handleCloseNoIrrigationModal = () => {
    setNoIrrigationModalOpen(false);
    setSearchText('');
    setSearchResults([]);
  };

  const handleSearchSites = async (value) => {
    setSearchText(value);
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.post(`${props.url}/api/hmi/genericPostRequest?type=SEARCH_JOBSITES_FOR_NO_IRRIGATION`, { search: value });
      if (response?.data?.success) {
        setSearchResults(response.data.data || []);
      }
    } catch (e) {
      console.error('Error searching sites', e);
    } finally {
      setSearching(false);
    }
  };

  const handleToggleNoIrrigation = async (jobSiteId, noIrrigationSystem) => {
    try {
      const response = await axios.post(`${props.url}/api/hmi/genericPostRequest?type=TOGGLE_NO_IRRIGATION`, { jobSiteId, noIrrigationSystem });
      if (response?.data?.success) {
        fetchNoIrrigationSites();
        setSearchText('');
        setSearchResults([]);
      }
    } catch (e) {
      console.error('Error toggling no irrigation', e);
    }
  };

  // Render functions for dashboard sections
  const renderMetricCards = () => (
    <Grid container spacing={3} className={classes.overviewSection}>
      <Grid item xs={12} md={3} sm={6}>
        <Paper className={classes.paper}>
          <Box className={classes.statCard}>
            <Badge badgeContent={stats.outstandingIssues} color="error" className={classes.badge}>
              <Avatar className={classes.avatar} style={{ backgroundColor: theme.palette.error.main }}>
                <WarningIcon className={classes.avatarIcon} />
              </Avatar>
            </Badge>
            <Typography className={classes.statNumber}>{stats.outstandingIssues}</Typography>
            <Typography className={classes.statLabel}>Outstanding Issues</Typography>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={3} sm={6}>
        <Paper className={classes.paper}>
          <Box className={classes.statCard}>
            <Avatar className={classes.avatar} style={{ backgroundColor: theme.palette.success.main }}>
              <CheckCircleIcon className={classes.avatarIcon} />
            </Avatar>
            <Typography className={classes.statNumber}>{stats.completedChecksThisWeek}</Typography>
            <Typography className={classes.statLabel}>Checks Completed This Week</Typography>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={3} sm={6}>
        <Paper className={classes.paper}>
          <Box className={classes.statCard}>
            <Badge badgeContent={stats.scheduledChecksNextWeek} color="primary" className={classes.badge}>
              <Avatar className={classes.avatar} style={{ backgroundColor: theme.palette.info.main }}>
                <ScheduleIcon className={classes.avatarIcon} />
              </Avatar>
            </Badge>
            <Typography className={classes.statNumber}>{stats.scheduledChecksNextWeek}</Typography>
            <Typography className={classes.statLabel}>Upcoming Checks Next Week</Typography>
          </Box>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={3} sm={6}>
        <Paper className={classes.paper}>
          <Box className={classes.statCard}>
            <Badge badgeContent={stats.openWorkOrders} color="warning" className={classes.badge}>
              <Avatar className={classes.avatar} style={{ backgroundColor: theme.palette.warning.main }}>
                <BuildIcon className={classes.avatarIcon} />
              </Avatar>
            </Badge>
            <Typography className={classes.statNumber}>{stats.openWorkOrders}</Typography>
            <Typography className={classes.statLabel}>Open Work Orders</Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderOutstandingIssues = () => (
    <Paper className={classes.paper}>
      <CardHeader 
        title="Outstanding Issues" 
        className={`${classes.cardHeader} ${classes.warningHeader}`}
        action={
          <Button 
            size="small" 
            color="inherit"
            endIcon={<NavigateNextIcon />}
            component={Link}
            to="/issues"
          >
            View All
          </Button>
        }
        classes={{
          action: classes.cardHeaderAction
        }}
      />
  
      <CardContent className={classes.cardContent}>
        <List component="div">
          {loading ? (
            <Box className={classes.progressWrapper}>
              <CircularProgress />
            </Box>
          ) : (
            issues.slice(0, 10).map(issue => (
              <ListItem
                key={issue._id}
                className={classes.listItemWithBorder}
                style={{ 
                  borderLeftColor: issue.priority === 'High' ? theme.palette.error.main :
                                  issue.priority === 'Medium' ? theme.palette.warning.main :
                                  theme.palette.success.main
                }}
                button
                component={Link}
                to={`/issues/${issue._id}`}
              >
                <ListItemAvatar>
                  <Avatar className={classes.propertyAvatar}>
                    <BusinessIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={issue.jobSite?.name || 'Unknown Property'}
                  secondary={
                    <React.Fragment>
                      <WarningIcon fontSize="small" className={classes.issueIcon} />
                      {issue.description || issue.type}
                    </React.Fragment>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={issue.priority}
                    className={classes.chip}
                    size="small"
                    color={
                      issue.priority === 'High' ? 'error' :
                      issue.priority === 'Medium' ? 'warning' : 'success'
                    }
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>
      </CardContent>
    </Paper>
  );
  
  
  // const renderOutstandingIssues = () => (
  //   <Paper className={classes.paper}>
  //     <CardHeader 
  //       title="Outstanding Issues" 
  //       className={`${classes.cardHeader} ${classes.warningHeader}`}
  //       action={
  //         <Button 
  //           size="small" 
  //           color="inherit"
  //           endIcon={<NavigateNextIcon />}
  //           component={Link}
  //           to="/issues" // Update with your actual route
  //         >
  //           View All
  //         </Button>
  //       }
  //       classes={{
  //         action: classes.cardHeaderAction
  //       }}
  //     />
  //     <Tabs
  //       value={issueTabValue}
  //       onChange={handleIssueTabChange}
  //       indicatorColor="primary"
  //       textColor="primary"
  //       variant="fullWidth"
  //       className={classes.tabRoot}
  //     >
  //       <Tab label="High Priority" />
  //       <Tab label="Medium Priority" />
  //       <Tab label="Low Priority" />
  //     </Tabs>
  //     <CardContent className={classes.cardContent}>
  //       <List component="div">
  //         {loading ? (
  //           <Box className={classes.progressWrapper}>
  //             <CircularProgress />
  //           </Box>
  //         ) : (
  //           issues
  //             .filter(issue => {
  //               return issue.priority === (issueTabValue === 0 ? 'High' : issueTabValue === 1 ? 'Medium' : 'Low');
  //             })
  //             .slice(0, 5)
  //             .map(issue => (
  //               <ListItem
  //                 key={issue._id}
  //                 className={classes.listItemWithBorder}
  //                 style={{ 
  //                   borderLeftColor: issue.priority === 'High' ? theme.palette.error.main :
  //                                   issue.priority === 'Medium' ? theme.palette.warning.main :
  //                                   theme.palette.success.main
  //                 }}
  //                 button
  //                 component={Link}
  //                 to={`/issues/${issue._id}`} // Update with your actual route
  //               >
  //                 <ListItemAvatar>
  //                   <Avatar className={classes.propertyAvatar}>
  //                     <BusinessIcon />
  //                   </Avatar>
  //                 </ListItemAvatar>
  //                 <ListItemText
  //                   primary={issue.jobSite?.name || 'Unknown Property'}
  //                   secondary={
  //                     <React.Fragment>
  //                       <WarningIcon fontSize="small" className={classes.issueIcon} />
  //                       {issue.description || issue.type}
  //                     </React.Fragment>
  //                   }
  //                 />
  //                 <ListItemSecondaryAction>
  //                   <Chip
  //                     label={issue.priority}
  //                     className={classes.chip}
  //                     size="small"
  //                     color={
  //                       issue.priority === 'High' ? 'error' :
  //                       issue.priority === 'Medium' ? 'warning' : 'success'
  //                     }
  //                   />
  //                 </ListItemSecondaryAction>
  //               </ListItem>
  //             ))
  //         )}
  //       </List>
  //     </CardContent>
  //     {!loading && issues.filter(i => 
  //       i.priority === (issueTabValue === 0 ? 'High' : issueTabValue === 1 ? 'Medium' : 'Low')
  //     ).length > 5 && (
  //       <Box className={classes.cardAction}>
  //         <Button
  //           variant="outlined"
  //           color="primary"
  //           size="small"
  //           className={classes.viewMoreButton}
  //           component={Link}
  //           to="/issues" // Update with your actual route
  //         >
  //           View More
  //         </Button>
  //       </Box>
  //     )}
  //   </Paper>
  // );

  const renderScheduleContent = () => {
    if (scheduleLoading) {
      return (
        <Box className={classes.progressWrapper} style={{ padding: 24 }}>
          <CircularProgress />
        </Box>
      );
    }

    const schedule = inspectionSchedule?.schedule;
    if (!inspectionSchedule || !schedule || schedule.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" p={3}>
          <CheckCircleIcon style={{ fontSize: 48, color: theme.palette.success.main, marginBottom: 8 }} />
          <Typography variant="body1" gutterBottom>No sites remaining — all inspections complete!</Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchInspectionSchedule}
            style={{ marginTop: 8 }}
          >
            Refresh Schedule
          </Button>
        </Box>
      );
    }

    const totalSitesScheduled = inspectionSchedule.totalSitesScheduled || schedule.reduce((sum, d) => sum + (d.stops?.length || 0), 0);
    const totalSitesRemaining = inspectionSchedule.totalSitesRemaining || 0;

    return (
      <>
        <Box px={2} py={1} style={{ backgroundColor: theme.palette.grey[50], borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" color="textSecondary">
            <strong>{totalSitesScheduled}</strong> sites over <strong>{schedule.length}</strong> day{schedule.length !== 1 ? 's' : ''}
            {totalSitesRemaining > 0 && <> | <strong>{totalSitesRemaining}</strong> remaining</>}
          </Typography>
        </Box>
        <List component="div" disablePadding>
          {schedule.map((day, dayIndex) => {
            const isExpanded = expandedDays[dayIndex] !== false;
            const dayDate = moment(day.date);
            const isToday = dayDate.isSame(moment(), 'day');
            const stopCount = day.stops?.length || 0;

            return (
              <React.Fragment key={dayIndex}>
                <ListItem
                  button
                  onClick={() => toggleDayExpanded(dayIndex)}
                  style={{
                    backgroundColor: isToday ? theme.palette.info.light + '22' : theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        Day {dayIndex + 1} — {dayDate.format('dddd, MMM D')}
                        {isToday && <Chip label="Today" size="small" color="primary" style={{ marginLeft: 8, height: 20 }} />}
                      </Typography>
                    }
                    secondary={`${stopCount} stop${stopCount !== 1 ? 's' : ''}${day.totalDriveMinutes ? ` | ${Math.round(day.totalDriveMinutes)} min driving` : ''}${day.totalDriveMiles ? ` | ${day.totalDriveMiles.toFixed(1)} mi` : ''}`}
                  />
                  <Tooltip title="View schedule details">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setScheduleDayDetail(day); }}
                      style={{ marginRight: 4 }}
                    >
                      <ScheduleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(day.stops || []).map((stop, stopIndex) => (
                      <ListItem
                        key={stop.siteId || stopIndex}
                        style={{ paddingLeft: 32, borderBottom: `1px solid ${theme.palette.divider}20` }}
                        dense
                      >
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: theme.palette.info.light, width: 32, height: 32 }}>
                            <Typography variant="caption" style={{ fontWeight: 'bold' }}>{stopIndex + 1}</Typography>
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {stop.siteName || 'Unknown Site'}
                              {stop.buildingNumber ? ` (Bldg ${stop.buildingNumber})` : ''}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="textSecondary">
                              {stop.arrivalTime && stop.departureTime
                                ? `${moment(stop.arrivalTime).format('h:mm A')} → ${moment(stop.departureTime).format('h:mm A')}`
                                : ''}
                              {stop.driveTimeMinutes ? ` | ${Math.round(stop.driveTimeMinutes)} min drive` : ''}
                              {stop.driveDistanceMiles ? ` | ${stop.driveDistanceMiles.toFixed(1)} mi` : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                    {day.returnToYard && (
                      <ListItem style={{ paddingLeft: 32, opacity: 0.6 }} dense>
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: theme.palette.grey[300], width: 32, height: 32 }}>
                            <HomeIcon style={{ fontSize: 16 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" color="textSecondary">Return to yard</Typography>}
                          secondary={
                            <Typography variant="caption" color="textSecondary">
                              {day.returnToYard.driveTimeMinutes ? `${Math.round(day.returnToYard.driveTimeMinutes)} min drive` : ''}
                              {day.returnToYard.driveDistanceMiles ? ` | ${day.returnToYard.driveDistanceMiles.toFixed(1)} mi` : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </>
    );
  };

  const renderScheduleDayDetailModal = () => {
    const day = scheduleDayDetail;
    if (!day) return null;

    const stops = day.stops || [];
    const HMI_YARD_LABEL = 'HMI Yard';
    const COLORS = {
      YARD: '#546e7a',
      DRIVING: '#1976d2',
      INSPECTION: '#2e7d32',
      STOP_BORDER: '#d32f2f',
      RETURN: '#546e7a'
    };

    // Build timeline entries: yard depart → (drive → stop)* → drive → yard return
    const entries = [];

    // Yard departure — workday starts at 6:00 AM
    entries.push({ type: 'stop', label: HMI_YARD_LABEL, subLabel: 'Depart', time: '6:00 AM', isYard: true, icon: 'home' });

    stops.forEach((stop, i) => {
      // Drive segment before this stop
      entries.push({
        type: 'drive',
        durationMin: stop.driveTimeMinutes || 0,
        distanceMiles: stop.driveDistanceMiles || 0
      });
      // Stop
      entries.push({
        type: 'stop',
        label: stop.siteName || 'Unknown Site',
        subLabel: stop.buildingNumber ? `Bldg ${stop.buildingNumber}` : (stop.communityName || stop.address || ''),
        time: stop.arrivalTime || '',
        endTime: stop.departureTime || '',
        durationMin: stop.inspectionTimeMinutes || 110,
        stopNumber: i + 1,
        isYard: false,
        icon: 'location'
      });
    });

    // Drive back to yard
    if (day.returnToYard) {
      entries.push({
        type: 'drive',
        durationMin: day.returnToYard.driveTimeMinutes || 0,
        distanceMiles: day.returnToYard.driveDistanceMiles || 0
      });
      entries.push({ type: 'stop', label: HMI_YARD_LABEL, subLabel: 'Return', time: day.returnToYard.estimatedArrival || '', isYard: true, icon: 'home' });
    }

    const dayDate = moment(day.date);

    return (
      <Dialog
        open={!!scheduleDayDetail}
        onClose={() => setScheduleDayDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}
      >
        <DialogTitle style={{ paddingBottom: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Day {day.day} — {dayDate.format('dddd, MMM D')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stops.length} stop{stops.length !== 1 ? 's' : ''} &middot; {Math.round(day.totalDriveMinutes || 0)} min driving &middot; {(day.totalDriveMiles || 0).toFixed(1)} mi total
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setScheduleDayDetail(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: '16px 24px', maxHeight: 520, overflowY: 'auto' }}>
          <Box style={{ position: 'relative', paddingLeft: 56 }}>
            {entries.map((entry, idx) => {
              if (entry.type === 'drive') {
                return (
                  <Box key={`drive-${idx}`} style={{ position: 'relative', paddingLeft: 28, minHeight: 40, display: 'flex', alignItems: 'center' }}>
                    {/* Dashed blue vertical line */}
                    <Box style={{
                      position: 'absolute',
                      left: 11,
                      top: 0,
                      bottom: 0,
                      width: 0,
                      borderLeft: `2px dashed ${COLORS.DRIVING}`
                    }} />
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                      <DirectionsCarIcon style={{ fontSize: 16, color: COLORS.DRIVING, marginRight: 6 }} />
                      <Typography variant="caption" style={{ color: COLORS.DRIVING, fontWeight: 600, fontStyle: 'italic' }}>
                        driving {entry.durationMin} min{entry.distanceMiles ? ` · ${entry.distanceMiles.toFixed(1)} mi` : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              }

              // Stop entry
              const isYard = entry.isYard;
              const circleColor = isYard ? COLORS.YARD : COLORS.STOP_BORDER;
              const circleSize = isYard ? 24 : 24;

              return (
                <Box key={`stop-${idx}`} style={{ position: 'relative', paddingLeft: 28, minHeight: 56, paddingBottom: 4, paddingTop: 4 }}>
                  {/* Vertical line connecting to next entry */}
                  {idx < entries.length - 1 && (
                    <Box style={{
                      position: 'absolute',
                      left: 11,
                      top: circleSize / 2,
                      bottom: 0,
                      width: 0,
                      borderLeft: `2px solid ${theme.palette.grey[300]}`
                    }} />
                  )}
                  {/* Numbered circle / yard icon */}
                  <Box style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: circleSize,
                    height: circleSize,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    border: `2px solid ${circleColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2
                  }}>
                    {isYard ? (
                      <HomeIcon style={{ fontSize: 14, color: COLORS.YARD }} />
                    ) : (
                      <Typography style={{ fontSize: 11, fontWeight: 700, color: circleColor }}>{entry.stopNumber}</Typography>
                    )}
                  </Box>
                  {/* Time column */}
                  <Typography variant="caption" color="textSecondary" style={{
                    position: 'absolute',
                    right: '100%',
                    top: 0,
                    marginRight: 8,
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    width: 52,
                    textAlign: 'right'
                  }}>
                    {entry.time || ''}
                  </Typography>
                  {/* Content */}
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {entry.label}
                    </Typography>
                    {entry.subLabel && (
                      <Typography variant="caption" color="textSecondary">{entry.subLabel}</Typography>
                    )}
                    {!isYard && entry.durationMin > 0 && (
                      <Box mt={0.5}>
                        <Chip
                          size="small"
                          label={`${Math.floor(entry.durationMin / 60)}h ${entry.durationMin % 60}m`}
                          style={{
                            backgroundColor: COLORS.INSPECTION + '22',
                            color: COLORS.INSPECTION,
                            fontWeight: 600,
                            height: 22,
                            fontSize: 11
                          }}
                        />
                        {entry.endTime && (
                          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                            until {entry.endTime}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions style={{ padding: '8px 16px' }}>
          <Box flex={1} display="flex" alignItems="center">
            <Typography variant="caption" color="textSecondary">
              {stops.length * (stops[0]?.inspectionTimeMinutes || 110)} min inspection &middot; {Math.round(day.totalDriveMinutes || 0)} min driving
            </Typography>
          </Box>
          <Button onClick={() => setScheduleDayDetail(null)} color="primary" variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderIrrigationChecks = () => (
    <Paper className={classes.paper} style={{ height: 'auto' }}>
      <CardHeader
        title={checkTabValue === 0 ? "Recent Irrigation Checks" : "Inspection Schedule"}
        className={checkTabValue === 0 ? `${classes.cardHeader} ${classes.successHeader}` : `${classes.cardHeader} ${classes.infoHeader}`}
        action={
          <Box display="flex" alignItems="center">
            {checkTabValue === 1 && (
              <Tooltip title="Refresh schedule">
                <IconButton size="small" color="inherit" onClick={fetchInspectionSchedule} style={{ marginRight: 4 }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Button
              size="small"
              color="inherit"
              endIcon={<NavigateNextIcon />}
              component={Link}
              to={checkTabValue === 0 ? "/recent-checks" : "/upcoming-checks"}
            >
              View All
            </Button>
          </Box>
        }
        classes={{
          action: classes.cardHeaderAction
        }}
      />
      <Tabs
        value={checkTabValue}
        onChange={handleCheckTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        className={classes.tabRoot}
      >
        <Tab label="Recently Completed" />
        <Tab label="Inspection Schedule" />
      </Tabs>
      <CardContent className={classes.cardContent} style={checkTabValue === 1 ? { maxHeight: 400, overflowY: 'auto' } : {}}>
        {checkTabValue === 0 ? (
          <List component="div">
            {loading ? (
              <Box className={classes.progressWrapper}>
                <CircularProgress />
              </Box>
            ) : (
              recentChecks
                .slice(0, 5)
                .map(check => {
                  const isInspection = check.source === 'inspection';
                  const completedBy = check.work_completed_by
                    ? `${check.work_completed_by.firstName || ''} ${check.work_completed_by.lastName || ''}`.trim()
                    : check.inspectorName || '';
                  return (
                    <ListItem
                      key={check._id}
                      button
                      component={Link}
                      to={isInspection
                        ? `/irrigation/inspection/${check._id}?siteId=${check.jobSite?._id || check.siteId || ''}`
                        : `/clients/${check.client}/Job/${check.job}`}
                    >
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: theme.palette.success.light }}>
                          <CheckCircleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={check.jobSite?.name || 'Unknown Property'}
                        secondary={`${isInspection ? 'Inspection' : 'Completed'} on ${moment(check.work_complete_at).format('MMM D')} by ${completedBy}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={moment(check.work_complete_at).format('MMM D')}
                          className={classes.dateChip}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })
            )}
          </List>
        ) : (
          renderScheduleContent()
        )}
      </CardContent>
      {checkTabValue === 0 && !loading && recentChecks.length > 5 && (
        <Box className={classes.cardAction}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            className={classes.viewMoreButton}
            component={Link}
            to="/recent-checks"
          >
            View More
          </Button>
        </Box>
      )}
    </Paper>
  );
  
  const renderWorkOrders = () => (
    <Paper className={classes.paper}>
      <CardHeader 
        title={`Open Irrigation Work Orders (${workOrders.length})` }
        className={`${classes.cardHeader}`}
        action={
          <Button 
            size="small" 
            color="inherit"
            endIcon={<NavigateNextIcon />}
            component={Link}
            to="/irrigation-workorders"
          >
            View All
          </Button>
        }
        classes={{
          action: classes.cardHeaderAction
        }}
      />
      <CardContent className={classes.cardContent}>
        {loading ? (
          <Box className={classes.progressWrapper}>
            <CircularProgress />
          </Box>
        ) : (
          <List component="div">
            {workOrders.slice(0, 5).map(order => (
              <ListItem
                key={order._id}
                className={classes.listItemWithBorder}
                style={{ 
                  borderLeftColor: order.priority === 'High' ? theme.palette.error.main :
                                  order.priority === 'Medium' ? theme.palette.warning.main :
                                  theme.palette.success.main
                }}
                button
                onClick={() => {
                  setSelectedWorkorder(order);
                  setWorkorderModalOpen(true);
                }}
              >
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: theme.palette.warning.light }}>
                    <BuildIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={order.jobSite.name}
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="textPrimary">
                        {order.type}
                      </Typography>
                      {` - Scheduled: ${moment(order.scheduledDate).format('MMM D')}`}
                    </React.Fragment>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={getCrewName(order.crew)}
                    className={classes.chip}
                    size="small"
                    color={
                      order.priority === 'High' ? 'error' :
                      order.priority === 'Medium' ? 'warning' : 'success'
                    }
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
      {!loading && workOrders.length > 5 && (
        <Box className={classes.cardAction}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            className={classes.viewMoreButton}
            component={Link}
            to="/irrigation-workorders" // Update with your actual route
          >
            View More
          </Button>
        </Box>
      )}
    </Paper>
  );
  
  const getCrewName = (crew) => {
    if (!crew) return ''
    // order?.crew?.name
    const {
      name,
      foremanName
    } = crew
    if (!name && !foremanName) return ''
    let crewName = `${name}${foremanName ? ` (${foremanName})` : '' }`
    return crewName
  }

  const handleCloseWorkorderModal = () => {
    setWorkorderModalOpen(false);
    setSelectedWorkorder(null);
  };

  const renderTerritoryStats = () => {
    // console.log('renderTerritoryStats', territoryStats)
    // console.log('Crwes...', crews)
    // Prepare data for charts
    const territoryData = territoryStats.map(territory => {
      const crew = crews.find(c => c._id === territory.crewId);
      const crewName = getCrewName(crew);
    
      return {
        crewId: territory.crewId,
        name: crew.name || 'Unassigned',
        foreman: crew?.foremanName || '',
        properties: territory.propertyCount,
        fill: crewColorMap[territory.crewId] || theme.palette.primary.main, // bar color
      };
    });
    
    // console.log('territoryData...', territoryData)
    
    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <Paper style={{ padding: theme.spacing(1) }}>
            <Typography variant="body2" color="textPrimary">
              {label}
            </Typography>
            <Typography variant="body2">
              {`${payload[0].value} Properties`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`Tech: ${payload[0].payload.foreman}`}
            </Typography>
          </Paper>
        );
      }
      return null;
    };
    
    return (
      <Paper className={classes.paper}>
        <CardHeader 
          title="Properties Per Territory" 
          className={classes.cardHeader}
          action={
            <Button 
              size="small" 
              color="inherit"
              endIcon={<NavigateNextIcon />}
              component={Link}
              to="/territory-map" // Update with your actual route
            >
              View Map
            </Button>
          }
          classes={{
            action: classes.cardHeaderAction
          }}
        />
        <CardContent>
          {loading ? (
            <Box className={classes.progressWrapper}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={territoryData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 60
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                height={60}
                tick={props => {
                  const { x, y, payload } = props;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={16}
                        textAnchor="end"
                        fill="#666"
                        transform="rotate(-35)"
                        fontSize={10}
                      >
                        {payload.value}
                      </text>
                    </g>
                  );
                }}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar dataKey="properties" fill={theme.palette.primary.main} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Paper>
  );
};

const renderAssetMapStats = () => {
  // Prepare data for pie chart
  const assetMapData = [
    { name: 'Properties with Maps', value: stats.propertiesWithMaps },
    { name: 'Properties without Maps', value: stats.totalProperties - stats.propertiesWithMaps }
  ];
  
  const COLORS = [theme.palette.primary.main, theme.palette.grey[300]];
  
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return percent > 0.1 ? (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };
  
  return (
    <Paper className={classes.paper}>
      <CardHeader 
        title="Properties with Asset Maps" 
        className={classes.cardHeader}
        action={
          <Button 
            size="small" 
            color="inherit"
            endIcon={<NavigateNextIcon />}
            component={Link}
            to="/irrigationassetmaps"
          >
            View Maps
          </Button>
        }
        classes={{
          action: classes.cardHeaderAction
        }}
      />
      <CardContent>
        {loading ? (
          <Box className={classes.progressWrapper}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={assetMapData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {assetMapData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box textAlign="center" mt={2}>
              <Typography variant="body1">
                {stats.propertiesWithMaps} of {stats.totalProperties} properties have asset maps
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ({((stats.propertiesWithMaps / stats.totalProperties) * 100).toFixed(1)}% coverage)
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Paper>
  );
};

const renderTerritoryMap = () => (
  <Paper className={classes.paper}>
    <CardHeader 
      title="Territory Map" 
      className={classes.cardHeader}
      action={
        <Button 
          size="small" 
          color="inherit"
          endIcon={<NavigateNextIcon />}
          component={Link}
          to="/irrigationareas"
        >
          Full Map
        </Button>
      }
      classes={{
        action: classes.cardHeaderAction
      }}
    />
    <CardContent style={{ padding: 0, height: 300 }}>
      <TerritoryMapPreview 

      />
    </CardContent>
  </Paper>
);

// const renderTerritoryMapOLD = () => (
//   <Paper className={classes.paper}>
//     <CardHeader 
//       title="Territory Map" 
//       className={classes.cardHeader}
//     />
//     <CardContent style={{ padding: 0, height: '100%' }}>
//       <Box className={classes.mapPreviewContainer}>
//         <div style={{ 
//           height: '100%', 
//           minHeight: 250,
//           background: `linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7)), 
//                        url('https://via.placeholder.com/800x400?text=Map+Background')`,
//           backgroundSize: 'cover',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//           flexDirection: 'column',
//           padding: theme.spacing(2)
//         }}>
//           <MapIcon style={{ fontSize: 60, color: theme.palette.primary.main, marginBottom: theme.spacing(2) }} />
//           <Typography variant="body1" align="center" gutterBottom>
//             Interactive Territory Map
//           </Typography>
//           <Typography variant="body2" color="textSecondary" align="center">
//             View territories and their assigned properties
//           </Typography>
//         </div>
//         <Box className={classes.mapPreviewOverlay}>
//           <Button 
//             variant="contained" 
//             color="primary" 
//             size="small"
//             component={Link}
//             to="/irrigationareas"
//             endIcon={<NavigateNextIcon />}
//           >
//             Open Full Map
//           </Button>
//         </Box>
//       </Box>
//     </CardContent>
//   </Paper>
// );

const renderTrendChart = () => {
  console.log('renderTrendChart....', chartData)
  // Check if we have data to display
  if (loading) {
    return (
      <Box className={classes.progressWrapper}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!chartData || chartData.length === 0) {
    return (
      <Box className={classes.noDataWrapper}>
        <Typography variant="body2" color="textSecondary">
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Get all employee names from the first data point
  // Exclude the 'date' field
  const employeeNames = Object.keys(chartData[0]).filter(key => key !== 'date');
  
  // Generate a color palette - using a mix of theme colors
  const generateColorPalette = (count) => {
    const colorOptions = [
      { main: theme.palette.primary.main, light: theme.palette.primary.light },
      { main: theme.palette.secondary.main, light: theme.palette.secondary.light },
      { main: theme.palette.success.main, light: theme.palette.success.light },
      { main: theme.palette.info.main, light: theme.palette.info.light },
      { main: theme.palette.warning.main, light: theme.palette.warning.light },
      { main: theme.palette.error.main, light: theme.palette.error.light }
    ];
    
    // If we need more colors than available in the theme, generate additional colors
    const result = [];
    for (let i = 0; i < count; i++) {
      if (i < colorOptions.length) {
        result.push(colorOptions[i]);
      } else {
        // Use a modulo to cycle through colors with different opacities
        const baseColor = colorOptions[i % colorOptions.length];
        const opacity = 0.7 - (Math.floor(i / colorOptions.length) * 0.15);
        result.push({
          main: baseColor.main,
          light: baseColor.light + Math.floor(opacity * 100).toString(16)
        });
      }
    }
    return result;
  };
  
  const colors = generateColorPalette(employeeNames.length);
  
  return (
    <Paper className={classes.paper}>
      <CardHeader 
        title="Completed Workorders by Employee (30 Days)" 
        className={classes.cardHeader}
      />
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 10 }}
              interval={6}
            />
            <YAxis allowDecimals={false} />
            <RechartsTooltip />
            <Legend />
            
            {/* Dynamically render Area components for each employee */}
            {employeeNames.map((employeeName, index) => {
              // Skip rendering if employee name is empty or "Unassigned"
              if (!employeeName.trim() || employeeName === "Unassigned") {
                return null;
              }
              
              return (
                <Area 
                  key={employeeName}
                  type="monotone" 
                  dataKey={employeeName} 
                  name={employeeName.length > 15 ? employeeName.substring(0, 15) + '...' : employeeName} 
                  stroke={colors[index].main} 
                  fill={colors[index].light} 
                  stackId="1" // Stack the areas on top of each other
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Paper>
  );
};

const renderTrendChartOle = () => {
  // Check if we have data to display
  if (loading) {
    return (
      <Box className={classes.progressWrapper}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!chartData || chartData.length === 0) {
    return (
      <Box className={classes.noDataWrapper}>
        <Typography variant="body2" color="textSecondary">
          No data available for the selected period
        </Typography>
      </Box>
    );
  }
  
  // Get all crew names from the first data point
  // Exclude the 'date' field
  const crewNames = Object.keys(chartData[0]).filter(key => key !== 'date');
  
  // Generate colors for each crew - using a combination of theme colors
  // Adjust this as needed based on your theme
  const colorOptions = [
    theme.palette.primary,
    theme.palette.secondary,
    theme.palette.success,
    theme.palette.info,
    theme.palette.warning,
    theme.palette.error
  ];
  
  return (
    <Paper className={classes.paper}>
      <CardHeader 
        title="Completed Workorders by Crew (30 Days)" 
        className={`${classes.cardHeader} ${classes.infoHeader}`}
      />
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 10 }}
              interval={6}
            />
            <YAxis allowDecimals={false} />
            <RechartsTooltip />
            <Legend />
            
            {/* Dynamically render Area components for each crew */}
            {crewNames.map((crewName, index) => {
              const colorIndex = index % colorOptions.length;
              const color = colorOptions[colorIndex];
              
              return (
                <Area 
                  key={crewName}
                  type="monotone" 
                  dataKey={crewName} 
                  name={crewName} 
                  stroke={color.main} 
                  fill={color.light} 
                  stackId="1" // Stack the areas on top of each other
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Paper>
  );
};

const renderTrendChartOLD = () => {
  // Generate some mock data for the trend chart
  const trendData = [];
  // for (let i = 30; i >= 0; i--) {
  //   const date = moment().subtract(i, 'days');
  //   trendData.push({
  //     date: date.format('MMM D'),
  //     checks: Math.floor(Math.random() * 5) + 1,
  //     issues: Math.floor(Math.random() * 3),
  //   });
  // }
  // console.log('renderTrendChart....', chartData)
  
  return (
    <Paper className={classes.paper}>
      <CardHeader 
        title="Monthly Activity" 
        className={`${classes.cardHeader} ${classes.infoHeader}`}
      />
      <CardContent>
        {loading ? (
          <Box className={classes.progressWrapper}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={trendData}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }}
                interval={6}
              />
              <YAxis allowDecimals={false} />
              <RechartsTooltip />
              <Area type="monotone" dataKey="checks" name="Checks" stroke={theme.palette.success.main} fill={theme.palette.success.light} />
              <Area type="monotone" dataKey="issues" name="Issues" stroke={theme.palette.error.main} fill={theme.palette.error.light} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Paper>
  );
};

// Main render
return (
  <div className={classes.root}>
    <Typography variant="h4" component="h1" className={classes.dashboardTitle}>
      Irrigation Dashboard
      {/* <Icon path={mdiAccount}
        title="User Profile"
        size={1}
        horizontal
        vertical
        rotate={90}
        color="red"
        spin
      /> */}
    </Typography>
    
    {/* Key Metrics Cards */}
    {renderMetricCards()}
    
    {/* First Row - Issues and Checks */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {renderOutstandingIssues()}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderIrrigationChecks()}
      </Grid>
    </Grid>
    
    {/* Second Row - Work Orders and Territory */}
    <Grid container spacing={3} style={{ marginTop: theme.spacing(3) }}>
      <Grid item xs={12} md={6}>
        {renderWorkOrders()}
      </Grid>
      <Grid item xs={12} md={6}>
        {(crews?.length && territoryStats) && renderTerritoryStats()}
      </Grid>
    </Grid>
    
    {/* Third Row - Map and Asset Maps */}
    <Grid container spacing={3} style={{ marginTop: theme.spacing(3) }}>
      <Grid item xs={12} md={8}>
        {renderTerritoryMap()}
      </Grid>
      <Grid item xs={12} md={4}>
        {renderAssetMapStats()}
      </Grid>
    </Grid>
    
    {/* Fourth Row - Trend Chart */}
    <Grid container spacing={3} style={{ marginTop: theme.spacing(3) }}>
      <Grid item xs={12}>
        {renderTrendChart()}
      </Grid>
    </Grid>
    {/* <Grid container spacing={3} style={{ marginTop: theme.spacing(3) }}>
      <Grid item xs={12}>
        <WorkorderTrendChart chartData={chartData} loading={loading} theme={theme} classes={classes} />
      </Grid>
    </Grid> */}

    {/* No Irrigation Status Card */}
    <Grid container spacing={3} style={{ marginTop: theme.spacing(1) }}>
      <Grid item xs={12} sm={6} md={3}>
        <Paper className={classes.paper} onClick={handleOpenNoIrrigationModal}>
          <Box className={classes.statCard}>
            <Avatar className={classes.avatar} style={{ backgroundColor: theme.palette.grey[500] }}>
              <BlockIcon className={classes.avatarIcon} />
            </Avatar>
            <Typography className={classes.statNumber}>{stats.noIrrigationCount || 0}</Typography>
            <Typography className={classes.statLabel}>Properties Without Irrigation</Typography>
            <Typography variant="caption" color="textSecondary">Click to manage</Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>

    {/* No Irrigation Modal */}
    <Dialog open={noIrrigationModalOpen} onClose={handleCloseNoIrrigationModal} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Properties Without Irrigation</span>
        <IconButton size="small" onClick={handleCloseNoIrrigationModal}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Search to add */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search sites to add..."
          value={searchText}
          onChange={(e) => handleSearchSites(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: searching ? <CircularProgress size={16} /> : null
          }}
          style={{ marginBottom: 8 }}
        />
        {searchResults.length > 0 && (
          <Paper variant="outlined" style={{ marginBottom: 16, maxHeight: 200, overflow: 'auto' }}>
            <List dense>
              {searchResults.map(site => (
                <ListItem key={site._id} button onClick={() => handleToggleNoIrrigation(site._id, true)}>
                  <ListItemText
                    primary={site.name}
                    secondary={`${site.address || ''}${site.city ? `, ${site.city}` : ''} — ${site.client?.displayName || site.client?.companyName || ''}`}
                  />
                  <Chip label="Add" size="small" icon={<AddIcon />} color="primary" variant="outlined" />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <Divider style={{ marginBottom: 8 }} />
        <Typography variant="subtitle2" gutterBottom>Current List ({noIrrigationSites.length})</Typography>

        {noIrrigationLoading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : noIrrigationSites.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 16 }}>
            No sites flagged yet. Use the search above to add sites.
          </Typography>
        ) : (
          <List dense>
            {noIrrigationSites.map(site => (
              <ListItem key={site._id}>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: theme.palette.grey[300], width: 32, height: 32 }}>
                    <BlockIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={site.name}
                  secondary={`${site.address || ''}${site.city ? `, ${site.city}` : ''} — ${site.client?.displayName || site.client?.companyName || ''}`}
                />
                <Tooltip title="Remove from list (mark as having irrigation)">
                  <IconButton size="small" onClick={() => handleToggleNoIrrigation(site._id, false)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseNoIrrigationModal} color="primary">Close</Button>
      </DialogActions>
    </Dialog>

    {/* Schedule Day Detail Modal */}
    {renderScheduleDayDetailModal()}

    {/* Workorder Detail Modal */}
    <WorkorderDetailModal
      open={workorderModalOpen}
      workorder={selectedWorkorder}
      onClose={handleCloseWorkorderModal}
      url={props.url}
    />

  </div>
);
};

const mapStateToProps = (state) => {
return {
  url: state.url,
};
};

export default connect(mapStateToProps)(IrrigationDashboard);